import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IPoolPlayer, ITournamentPlayersPayload} from "data/providers/api/tournament.provider";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {PlayerStatus, PoolOrderedFields, SortOrder} from "data/enums";
import {chain, deburr, get, some} from "lodash";
import type {ICountryStore} from "data/stores/country/country.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ITeamStore} from "data/stores/team/team.store";

export interface IFilterForm extends HTMLFormElement {
	search: HTMLInputElement;
}

export interface IFilter {
	search: string;
	orderBy: PoolOrderedFields;
	direction: Record<PoolOrderedFields, SortOrder>;
	// avgOrderDirection: SortOrder;
	// ownedOrderDirection: SortOrder;
}

const DEFAULT_FILTER = {
	search: "",
	orderBy: PoolOrderedFields.Default,
	direction: {
		default: SortOrder.DESC,
		lastName: SortOrder.ASC,
		projPts: SortOrder.ASC,
		cuts: SortOrder.ASC,
		top5s: SortOrder.ASC,
		top10s: SortOrder.ASC,
		wins: SortOrder.ASC,
		avgPts: SortOrder.ASC,
		owned: SortOrder.ASC,
		oddsToWin: SortOrder.ASC,
	},
	// avgOrderDirection: SortOrder.ASC,
	// ownedOrderDirection: SortOrder.ASC,
};

export interface IPoolPlayersStore {
	get list(): IPoolPlayer[];
	get sortedPlayers(): IPoolPlayer[];
	get isOpen(): boolean;
	get filter(): IFilter;

	toggleHandler: () => void;
	closeHandler: () => void;
	setSearch(value: string): void;
	setOrderBy(value: PoolOrderedFields): void;
	setOrderDirection(value: PoolOrderedFields, order?: SortOrder): void;

	getPoolPlayerById(playerId: number): IPoolPlayer | undefined;
	fetchPoolPlayers(payload: ITournamentPlayersPayload): Promise<void>;
}

@injectable()
export class PoolPlayersStore implements IPoolPlayersStore {
	@observable private _list: IPoolPlayer[] = [];
	@observable private _isOpen: boolean = false;
	@observable private _filter: IFilter = DEFAULT_FILTER;

	get isOpen() {
		return this._isOpen;
	}

	get list() {
		return this._list;
	}

	get filter() {
		return this._filter;
	}

	private isListed = (player: IPoolPlayer) => {
		const {search} = this.filter;
		const fullName = deburr(`${player.firstName} ${player.lastName}`);

		return !search || fullName.toLowerCase().includes(deburr(search).toLowerCase());
	};

	get isTournamentDisabled() {
		if (this._tournamentsStore.isDisabledByCompleteRound) {
			return true;
		} else {
			return some([
				this._tournamentsStore.isSelectedTournamentDisabled,
				this._tournamentsStore.isDisabledChangeTeam,
				this._teamStore.hasPrevTeam,
			]);
		}
	}

	get sortedPlayers() {
		return chain(this.list)
			.filter((player) => player.status !== PlayerStatus.Inactive)
			.filter(this.isListed)
			.map((player) => {
				const isActivePlayer = player.status === PlayerStatus.Active;
				const isLineupFull = this._teamStore.isCurrentLineupFull;
				const isPlayerUsed3Times = this._tournamentsStore.getIsPlayerUsed3Times(player.id);

				const isAddDisabled = some([
					!isActivePlayer,
					this.isTournamentDisabled,
					isLineupFull,
					isPlayerUsed3Times,
				]);

				const isRemoveDisabled = some([!isActivePlayer, this.isTournamentDisabled]);

				return {
					...player,
					isAddDisabled,
					isRemoveDisabled,
				};
			})
			.orderBy(
				(item) => {
					if (this.filter.orderBy === PoolOrderedFields.Default) {
						return [item.status !== PlayerStatus.Withdrawn, !item.isAddDisabled];
					}

					if (this.filter.orderBy === PoolOrderedFields.OddsToWin) {
						return Number(get(item.oddsToWin, this._countryStore.country));
					}

					return item[this.filter.orderBy];
				},
				[this.filter.direction[this.filter.orderBy]]
			)
			.value();
	}

	constructor(
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.CountryStore) private _countryStore: ICountryStore,
		@inject(Bindings.TournamentsStore) private _tournamentsStore: ITournamentsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore
	) {
		makeAutoObservable(this);
	}

	getPoolPlayerById(playerId: number): IPoolPlayer | undefined {
		return this._list.find((player) => player.id === playerId);
	}

	toggleHandler = () => {
		this._isOpen = !this._isOpen;
	};

	closeHandler = () => {
		this._isOpen = false;
	};

	@action setSearch(value: string) {
		this._filter.search = value;
	}

	@action setOrderBy(value: PoolOrderedFields) {
		this._filter.orderBy = value;
	}

	@action setOrderDirection(value: PoolOrderedFields, order?: SortOrder) {
		if (order) {
			this._filter.direction[value] = order;
		} else {
			this._filter.direction[value] =
				this._filter.direction[value] === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
		}
	}

	@action
	async fetchPoolPlayers(payload: ITournamentPlayersPayload) {
		const {data} = await this._jsonProvider.tournamentPlayers(payload);

		runInAction(() => {
			this._list = data;
		});
	}
}
